import { type MeetingQuestion } from '../cubits/meeting/MeetingQuestion'
import { type SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'

/**
 * sort question by slide order, then by question order (per slide),
 * then by question type, then question text
 *
 * @param rubrics - Array of SlideQuestion objects to sort
 * @param slideIds - array of sorted slideIds, can pass empty if all questions are on the same slide
 * @param isGroupQuiz - if true, sort order is ignored (order only applied to individual quiz slides)
 * @returns Sorted array of SlideQuestion objects
 */
export function sortQuestions<T extends SlideQuestion | MeetingQuestion>({
  questions,
  slideIds: _slideIds,
  isGroupQuiz = false,
  sortNoSlideFirst = false,
}: {
  questions: T[]
  slideIds: Array<string>
  isGroupQuiz?: boolean
  sortNoSlideFirst?: boolean
}): T[] {
  // pre-meeting quiz must always be first
  const unshiftPreMeetingKey =
    !_slideIds.length || _slideIds[0] !== PRE_MEETING_QUESTION_SLIDE_ID

  const slideIds = unshiftPreMeetingKey
    ? [PRE_MEETING_QUESTION_SLIDE_ID, ..._slideIds]
    : _slideIds

  return [...questions].sort((a, b) => {
    const [
      {
        order: aOrder = Infinity,
        question: aQuestion,
        questionType: aQuestionType,
      },
      {
        order: bOrder = Infinity,
        question: bQuestion,
        questionType: bQuestionType,
      },
    ] = [a.data, b.data]

    const aSlideId = a.data.slideId || a.data.groupSlideId || ''
    const bSlideID = b.data.slideId || b.data.groupSlideId || ''

    const aTypeOrder = sortOrderFromQuestionType(aQuestionType)
    const bTypeOrder = sortOrderFromQuestionType(bQuestionType)

    let aIndex = slideIds.indexOf(aSlideId)
    let bIndex = slideIds.indexOf(bSlideID)

    // If slideId is not found, sorting priority moves to end for slide index
    if (aIndex === -1) aIndex = slideIds.length
    if (bIndex === -1) bIndex = slideIds.length

    // if sorting no slide first, move questions without slideId to the top
    if (sortNoSlideFirst) {
      const [aNoSlides, bNoSlides] = [!aSlideId, !bSlideID]
      if (aNoSlides !== bNoSlides) return aNoSlides ? -1 : 1
    }

    if (aIndex !== bIndex) return aIndex - bIndex // first sort by slide order (if present)
    if (aOrder !== bOrder && !isGroupQuiz) return aOrder - bOrder // then by per slide sort order (if not group quiz)
    if (aTypeOrder !== bTypeOrder) return aTypeOrder - bTypeOrder // then by question type
    return aQuestion.localeCompare(bQuestion) // finally by question text
  })
}

function sortOrderFromQuestionType(questionType: SlideQuestionType): number {
  switch (questionType) {
    case SlideQuestionType.title:
      return 1
    case SlideQuestionType.sorting:
    case SlideQuestionType.multipleChoice:
      return 2
    case SlideQuestionType.poll:
    case SlideQuestionType.customPoll: {
      return 3
    }

    case SlideQuestionType.sortableStringList:
      return 4

    case SlideQuestionType.popUpInstructions: {
      return 5
    }

    case SlideQuestionType.talkTrack: {
      return 6
    }

    case SlideQuestionType.moderatorBot:
    case SlideQuestionType.graderBot:
    case SlideQuestionType.rolePlayBot:
    case SlideQuestionType.tutorBot:
    case SlideQuestionType.formCompletionBot: {
      return 7
    }

    // these aren't answerable question, so the sort shouldn't matter
    // or even really be present in the first place
    case SlideQuestionType.moderationTask: {
      return 8
    }
    // these aren't answerable question, so the sort shouldn't matter
    // or even really be present in the first place
    case SlideQuestionType.displayModerationTask: {
      return 9
    }

    case SlideQuestionType.interactiveFormElement: {
      return 10
    }

    case SlideQuestionType.aiAgent: {
      return 11
    }

    case SlideQuestionType.suggestedTimer: {
      return 12
    }
  }
}
